/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { useFormik } from 'formik';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Button, FormControl, FormHelperText, OutlinedInput, Typography } from '@mui/material';

import { SimpleNotifyModal } from '../../../../../components/SimpleNotifyModal/SimpleNotifyModal';
import { Namespaces } from '../../../../../i18n';
import { SuperuserUserApiService } from '../../../../../services/SuperuserUserApiService';
import { RootState } from '../../../../../store';
import { useFormStyles } from '../../../../../styles/form';
import { ApiClientError } from '../../../../../types/api/ApiClientError';
import { useApiValidation } from '../../../../../utils/useApiValidation';
import { useObjectState } from '../../../../../utils/useObjectState';
import { useValidationTranslation } from '../../../../../utils/useValidationTranslation';
import { emailTestValidationSchema } from '../../../../EmailTestPage/EmailTestPage.const';
import { EmailTestFormValues } from '../../../../EmailTestPage/EmailTestPage.types';
import { useSetupPageStyles } from '../../../SetupPage.styles';

const mapState = (state: RootState) => ({
  auth: state.auth,
});
const connector = connect(mapState);
type PropsFromRedux = ConnectedProps<typeof connector>;

type EmailTestFormProps = PropsFromRedux & {
  onTestEmailSend: () => void;
};

type ModalState = {
  successModal: {
    isOpen: boolean;
  };
  errorModal: {
    isOpen: boolean;
    errorText?: string;
  };
};

const EmailTestFormComponent = memo<EmailTestFormProps>(props => {
  const { auth, onTestEmailSend } = props;
  const formClasses = useFormStyles();
  const classes = useSetupPageStyles();
  const { vtr } = useValidationTranslation();
  const { t } = useTranslation(Namespaces.Admin);

  const [isFetching, setIsFetching] = useState(false);
  const [modalState, setModalState] = useObjectState<ModalState>({
    successModal: {
      isOpen: false,
    },
    errorModal: {
      isOpen: false,
      errorText: '',
    },
  });

  const { hasFieldApiError, getFieldApiError, resetFieldApiError, resetApiError, setApiError } = useApiValidation();

  const initialValues: EmailTestFormValues = {
    email: auth.user?.email || '',
    subject: '',
    html: '',
  };

  const submit = async (values: EmailTestFormValues) => {
    resetApiError();
    setIsFetching(true);
    try {
      await SuperuserUserApiService.createEmailTest({ data: values });
      setModalState({
        successModal: {
          isOpen: true,
        },
      });
    } catch (e) {
      const error = e as ApiClientError;
      const responseData = error.response?.data;
      if (!responseData) {
        setModalState({
          errorModal: {
            isOpen: true,
          },
        });
      } else {
        setApiError(error);
        const jsonObject = JSON.stringify(responseData, null, 2);
        setModalState({
          errorModal: {
            isOpen: true,
            errorText: jsonObject,
          },
        });
      }
    } finally {
      setIsFetching(false);
    }
  };

  const formik = useFormik<EmailTestFormValues>({
    initialValues,
    validationSchema: emailTestValidationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: submit,
  });

  const { values, errors, touched, handleChange, handleBlur, handleSubmit } = formik;

  const hasError = (name: keyof EmailTestFormValues): boolean => {
    return !!touched[name] && (!!errors[name] || hasFieldApiError(name));
  };

  const getError = (name: keyof EmailTestFormValues): string => {
    const error = errors[name] || getFieldApiError(name);
    return error || '';
  };

  return (
    <form onSubmit={handleSubmit}>
      <Typography className={classes.testEmailInfo} variant="body1">
        <InfoOutlinedIcon />
        {t(`${Namespaces.Setup}:page.email_configuration.test_email_info`)}
      </Typography>
      <FormControl className={cx(formClasses.formControl, formClasses.widthMedium)} error={hasError('subject')}>
        <OutlinedInput
          name="subject"
          placeholder={t('email_test.subject')}
          value={values.subject}
          onChange={event => {
            resetFieldApiError('subject');
            handleChange(event);
          }}
          onBlur={handleBlur}
          disabled={isFetching}
        />
        {hasError('subject') && (
          <FormHelperText>
            {vtr(getError('subject'), {
              field: t('email_test.subject'),
            })}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl className={cx(formClasses.formControl, formClasses.widthMedium)} error={hasError('html')}>
        <OutlinedInput
          name="html"
          placeholder={t('email_test.html')}
          value={values.html}
          onChange={event => {
            resetFieldApiError('html');
            handleChange(event);
          }}
          onBlur={handleBlur}
          disabled={isFetching}
          multiline={true}
          minRows={3}
          maxRows={7}
        />
        {hasError('html') && (
          <FormHelperText>
            {vtr(getError('html'), {
              field: t('email_test.html'),
            })}
          </FormHelperText>
        )}
      </FormControl>
      <div className={classes.formActions}>
        <Button variant="outlined" onClick={onTestEmailSend}>
          {t(`${Namespaces.Setup}:page.buttons.skip`)}
        </Button>
        <Button type="submit" variant="contained" disabled={isFetching}>
          {t('buttons.submit')}
        </Button>
      </div>
      <SimpleNotifyModal
        open={modalState.successModal.isOpen}
        message={t('email_test.success-modal')}
        onOk={onTestEmailSend}
      />
      <SimpleNotifyModal
        open={modalState.errorModal.isOpen}
        message={
          modalState.errorModal.errorText ? (
            <pre className={classes.modalError}>{modalState.errorModal.errorText}</pre>
          ) : (
            t(`${Namespaces.Common}:errors.unexpected_error`)
          )
        }
        onOk={() =>
          setModalState({
            errorModal: { isOpen: false, errorText: '' },
          })
        }
      />
    </form>
  );
});

export const EmailTestForm = connector(EmailTestFormComponent);
